import React from "react";

import {
    SvgElement as BaseSvgElement,
    Props,
} from "@thelabnyc/thelabui/src/components/Svg";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./svg.module.scss";

export const utilityIcons = [
    "search",
    "account",
    "arrow",
    "close",
    "hamburger",
    "plus",
    "minus",
    "check",
    "caret",
    "triangle",
    "download",
    "share",
    "utility",
    "clock",
    "cards",
    "video/play",
    "video/pause",
    "audio on",
    "audio off",
    "filter",
] as const;
export const socialIcons = [
    "share/facebook",
    "share/instagram",
    "share/youtube",
    "share/pinterest",
    "share/twitter",
    "share/tiktok",
    "share/whatsapp",
    "share/linkedin",
    "share/url",
    "share/email",
] as const;

/**
 * thelab-ui provides some icons that might be useful, but you'll
 * definitely need project-specific icons. Put the names of those
 * icons here.
 */
export const localElementNames = [...utilityIcons, ...socialIcons] as const;

/**
 * Project-specific SVGs plus SVGs from thelab-ui.
 *
 * This deduplicates, so if you put something into SvgElementNames that's in
 * labuiSvgNames, it'll only show up once. This is useful if you want
 * to override something from thelab-ui.
 */
export const allSvgNames = [...new Set([...localElementNames].sort())] as const;

type SVGName = (typeof allSvgNames)[number];

const SvgElement = (props: React.ComponentProps<typeof BaseSvgElement>) => {
    return (
        <BaseSvgElement
            {...props}
            className={concatClassNames([props.className, styles.icon])}
        />
    );
};

/**
 * This repo's solution for using icon-like SVGs. "Icon-like" means that the SVG
 * is a relatively small and simple graphic. Most, if not all of our icons are
 * monochromatic, their fills are modified with CSS, and they might be reused in
 * different contexts around the site.
 *
 * This is building around a lab-ui base Svg component, which means that some of
 * these icons are not specific to this repository and might not be what we want
 * to use. They can be overridden if necessary.
 *
 * To add an SVG:
 * - Add a name to `SvgElementNames`
 * - Make the SVG TSX-friendly. https://react-svgr.com/playground/?typescript=true
 *   can help with this
 * - Add a case with your name and your element, replacing `<svg>` with `<SvgElement>`.
 *   You should only need `{...props}` and the `viewBox` attribute; height/width and
 *   fill color can be set with CSS
 *
 * You can hardcode fill colors, but you'll need to be very sure that the color will
 * not change. Typically the logo is a place where you'd do this.
 *
 * Some images might be in the SVG format but not fit this mold, and can be treated as
 * static images instead (placed in the static folder and loaded with `<img>`).
 */
export const Svg = ({ name, ...props }: Props<SVGName>) => {
    switch (name) {
        case "search":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M10.687 2c4.783 0 8.687 3.823 8.687 8.57 0 2.14-.795 4.09-2.104 5.588l4.666 4.593-1.425 1.403-4.723-4.65a8.73 8.73 0 0 1-5.1 1.637C5.903 19.14 2 15.318 2 10.57 2 5.822 5.904 2 10.687 2m0 2C6.98 4 4.001 6.956 4.001 10.57s2.979 6.57 6.686 6.57 6.687-2.955 6.687-6.57c0-3.614-2.98-6.57-6.687-6.57" />
                </SvgElement>
            );
        case "account":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M12.532 1.008c3.24.167 5.814 2.892 5.814 6.231l-.008.322a6.26 6.26 0 0 1-2.445 4.67C20.288 13.78 23.43 18.025 23.432 23h-2c0-5.207-4.144-9.398-9.216-9.398C7.143 13.602 3 17.793 3 23H1c0-4.974 3.144-9.22 7.538-10.77a6.26 6.26 0 0 1-2.443-4.67l-.008-.32C6.087 3.791 8.83 1 12.217 1zM12.217 3c-2.249 0-4.13 1.863-4.13 4.24 0 2.375 1.881 4.239 4.13 4.239s4.129-1.864 4.129-4.24S14.465 3 12.216 3" />
                </SvgElement>
            );
        case "arrow":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="m16.151 18.5 4.895-5.611H1V11.08h20.014L16.151 5.5h2.089l5.767 6.484L18.24 18.5z" />
                </SvgElement>
            );
        case "close":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M20.707 4.707 13.414 12l7.293 7.293-1.414 1.414L12 13.414l-7.293 7.293-1.414-1.414L10.586 12 3.293 4.707l1.414-1.414L12 10.586l7.293-7.293z" />
                </SvgElement>
            );
        case "hamburger":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M20 20H4v-2h16zm0-7H4v-2h16zm0-7H4V4h16z" />
                </SvgElement>
            );
        case "plus":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M12.9 11h8.899v2h-8.9v8.9h-2V13H2v-2h8.9V2.1h2z" />
                </SvgElement>
            );
        case "minus":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M21.799 13H2v-2h19.799z" />
                </SvgElement>
            );
        case "check":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M21.23 8.065 9.84 19.456l-.073.067a1 1 0 0 1-1.341-.067L2.77 13.8l1.414-1.414 4.95 4.95L19.816 6.651z" />
                </SvgElement>
            );
        case "caret":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="m17.669 11.257.826.743-.826.743-10 9-1.338-1.486L15.506 12 6.33 3.743 7.67 2.257z" />
                </SvgElement>
            );
        case "triangle":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M12 22L1.608 4h20.784L12 22z" />
                </SvgElement>
            );
        case "download":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M20 24H4v-2h16zm-7-7.414 5.293-5.293 1.414 1.414L12 20.414l-7.707-7.707 1.414-1.414L11 16.586V2h2z" />
                </SvgElement>
            );
        case "share":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M7.794 11.39H5.725V21.63h12.55V11.39h-2.069V9.52h3.004a.937.937 0 0 1 .936.935v12.11c0 .516-.419.935-.936.935H4.79a.936.936 0 0 1-.936-.936V10.455l.005-.092a.936.936 0 0 1 .93-.843h3.005Zm9.55-5.55L16.02 7.163l-3.085-3.085V15.39h-1.872V4.086L7.987 7.163 6.664 5.84 12.004.5Z" />
                </SvgElement>
            );
        case "utility":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M4 10a2 2 0 1 1 0 4 2 2 0 0 1 0-4m8 0a2 2 0 1 1 0 4 2 2 0 0 1 0-4m8 0a2 2 0 1 1 0 4 2 2 0 0 1 0-4" />
                </SvgElement>
            );
        case "clock":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M12 1c6.075 0 11 4.925 11 11l-.015.566C22.691 18.378 17.885 23 12 23l-.566-.015C5.809 22.7 1.3 18.191 1.014 12.566L1 12C1 5.925 5.925 1 12 1m0 2a9 9 0 1 0 0 18 9 9 0 0 0 0-18m0 3a1 1 0 0 1 1 1v4.586l3.707 3.707a1 1 0 1 1-1.414 1.414l-4-4A1 1 0 0 1 11 12V7a1 1 0 0 1 1-1" />
                </SvgElement>
            );
        case "cards":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M16.991 5.296c.558 0 1.01.452 1.01 1.01v15.98a1.01 1.01 0 0 1-1.01 1.01H1.011a1.01 1.01 0 0 1-1.005-.906L0 22.286V6.306c0-.558.453-1.01 1.01-1.01zm-14.99 16h14v-14h-14zM22.787 0c.558 0 1.01.452 1.01 1.01v16.98c0 .523-.397.953-.906 1.005l-.104.005H19.31v-2h2.487V2h-15v2.036h-2V1.01c0-.558.452-1.01 1.01-1.01z" />
                </SvgElement>
            );
        case "video/play":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M22 12 4 22.392V1.608z" />
                </SvgElement>
            );
        case "video/pause":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M9.5 20h-4V4h4zm9 0h-4V4h4z" />
                </SvgElement>
            );
        case "audio on":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="m13.816 1-10 7h-3v8h3l10 7zm6.125.723-.83.556S22.027 6.66 22.027 12s-2.916 9.723-2.916 9.723l.83.556S23.027 17.704 23.027 12 19.941 1.723 19.941 1.723M17.023 5.39l-.824.566s1.912 2.813 1.912 6.217S16.2 18.39 16.2 18.39l.824.566s2.088-3.008 2.088-6.783-2.088-6.783-2.088-6.783" />
                </SvgElement>
            );
        case "audio off":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M22.639.154 20.45 2.441c-.255-.449-.431-.718-.431-.718l-.83.556s.222.345.535.922L17.31 5.725c-.12-.198-.21-.334-.21-.334l-.824.566s.128.188.315.52l-2.697 2.82V1l-10 7h-3v8h3l2.148 1.504-5.404 5.65.722.692 5.512-5.762L13.895 23V10.742l3.171-3.316c.528 1.145 1.123 2.843 1.123 4.748 0 3.404-1.912 6.217-1.912 6.217l.825.566s2.087-3.008 2.087-6.783c0-2.296-.771-4.303-1.377-5.528l2.393-2.5c.842 1.773 1.9 4.634 1.9 7.854 0 5.342-2.916 9.723-2.916 9.723l.83.556S23.106 17.704 23.106 12c0-3.634-1.25-6.803-2.16-8.629L23.361.846z" />
                </SvgElement>
            );
        case "share/facebook":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0m1.06 5.502c.666-.013 1.355.065 1.96.025V7.72h-1.516c-.191 0-.506.266-.506.46v1.557h2.065l-.22 2.278h-1.845V18.5h-2.723v-6.486H9V9.736h1.273c.007-1.3-.164-2.798.967-3.705.512-.41 1.155-.516 1.82-.529" />
                </SvgElement>
            );
        case "share/instagram":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0m-.012 5.5c1.766-.003 1.988.005 2.68.035s1.164.139 1.578.299c.428.165.79.387 1.152.748.362.36.585.721.752 1.148.162.414.272.885.305 1.579.033.693.042.914.045 2.68.003 1.764-.005 1.985-.035 2.679-.03.691-.139 1.165-.299 1.578-.165.428-.388.79-.748 1.152a3.2 3.2 0 0 1-1.148.752c-.414.162-.883.272-1.577.305-.693.033-.915.042-2.68.045-1.764.003-1.987-.005-2.679-.035s-1.165-.14-1.578-.299a3.2 3.2 0 0 1-1.152-.748 3.2 3.2 0 0 1-.752-1.148c-.163-.413-.274-.887-.307-1.579-.034-.693-.042-.914-.045-2.68-.003-1.764.005-1.987.035-2.679s.139-1.164.299-1.578c.165-.428.388-.79.748-1.152s.721-.584 1.148-.75c.413-.163.887-.274 1.579-.307.693-.034.914-.042 2.68-.045m.002 1.174c-1.735.004-1.941.01-2.627.043-.633.03-.976.135-1.205.224-.303.119-.521.26-.748.489a2 2 0 0 0-.484.748c-.088.229-.194.574-.22 1.207-.031.685-.038.891-.034 2.627s.01 1.94.043 2.625c.03.634.137.977.226 1.207.119.303.258.52.487.746s.444.367.748.484c.23.088.575.195 1.209.223.685.03.889.035 2.625.031s1.941-.01 2.627-.043c.633-.03.976-.135 1.205-.224.303-.12.52-.26.748-.489s.367-.444.484-.748c.089-.23.193-.573.22-1.207.03-.685.037-.892.034-2.627s-.01-1.94-.043-2.625c-.03-.634-.137-.978-.226-1.207a2 2 0 0 0-.487-.746 2 2 0 0 0-.75-.484c-.229-.089-.572-.193-1.205-.221-.686-.03-.892-.037-2.627-.033m3.47 1.07a.78.78 0 1 1 .003 1.561.78.78 0 0 1-.002-1.56m-3.466.918a3.338 3.338 0 1 1 .012 6.676 3.338 3.338 0 0 1-.012-6.676m.002 1.172a2.166 2.166 0 1 0 .007 4.331 2.166 2.166 0 0 0-.007-4.331" />
                </SvgElement>
            );
        case "share/youtube":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0m-.871 7c1.133-.006 2.267.02 3.394.055 2.276.07 3.881-.386 4.186 2.427a25.6 25.6 0 0 1 0 5.051c-.309 2.854-2.04 2.361-4.33 2.428-1.954.057-3.883.06-5.832-.045-.995-.054-2.15.02-2.629-1.06-.541-1.222-.426-3.906-.36-5.268.049-.98.018-2.458.885-3.102.654-.485 2.69-.443 3.553-.466q.567-.016 1.133-.02m-.645 2.506v5.004l4.16-2.526z" />
                </SvgElement>
            );
        case "share/pinterest":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0m.291 6c1.038.009 2.07.306 2.906.91 2.1 1.515 1.872 5.115.198 6.85-.718.743-1.809 1.154-2.848.969-.493-.089-.903-.483-1.322-.731-.078.005-.325 1.313-.38 1.508-.192.689-.482 1.47-.947 2.021-.072.087-.499.555-.593.46-.114-1.117-.037-2.204.175-3.301.17-.87.682-2.126.743-2.934.011-.152-.226-.627-.256-.87-.13-1.027.724-2.863 1.986-2.032.588.386.354 1.159.215 1.72-.208.84-1.11 2.588.207 2.926 2.514.646 3.75-4.624 1.393-5.97-2.234-1.277-5.41.679-5.108 3.257.07.6.633 1.074.592 1.623-.015.206-.15.36-.168.563-2.22-.33-2.086-3.195-1.176-4.703C8.811 6.77 10.561 5.985 12.291 6" />
                </SvgElement>
            );
        case "share/twitter":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0M6.223 6h3.355l3.037 4.355.156-.056L16.445 6h.987l-4.301 5.07 4.7 6.825-.054.105h-3.355l-3.145-4.51-.154-.11L7.129 18h-.932l4.471-5.336L6.17 6.105zm1.359.797 7.32 10.459h1.438l.053-.106L9.1 6.797z" />
                </SvgElement>
            );
        case "share/tiktok":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0m.338 5h2.377c-.004 0-.027.23.033.586h-.004c.072.429.267 1.042.79 1.646q.313.344.698.602.133.087.274.16c.608.307 1.201.4 1.494.367v2.387s-.835-.035-1.455-.201a5.5 5.5 0 0 1-1.416-.592s-.385-.256-.414-.273v4.931c0 .274-.072.96-.287 1.532-.189.489-.458.942-.797 1.34 0 0-.53.664-1.463 1.11-.84.404-1.58.396-1.8.405 0 0-1.277.05-2.429-.74l-.005-.006v.006a4.9 4.9 0 0 1-1.03-.983 4.7 4.7 0 0 1-.646-1.19v-.005c-.092-.277-.285-.95-.256-1.598.046-1.14.425-1.844.525-2.02.264-.479.61-.909 1.02-1.269a4.372 4.372 0 0 1 3.402-1.006l-.002 2.448c-.2-.066-.41-.098-.619-.098-1.108 0-2.004.913-2.004 2.041s.896 2.041 2.004 2.041c.345 0 .685-.09.985-.262a2.03 2.03 0 0 0 1.015-1.62v-.005l.002-.021.002-.045q.007-.183.006-.371z" />
                </SvgElement>
            );
        case "share/whatsapp":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0m0 4.5c4.135 0 7.504 3.365 7.504 7.5s-3.365 7.5-7.5 7.5a7.5 7.5 0 0 1-3.541-.89l-3.555.88c-.026.01-.052.01-.078.01a.324.324 0 0 1-.316-.414l.95-3.404A7.5 7.5 0 0 1 4.5 12c0-4.135 3.365-7.5 7.5-7.5M9.273 8.416c-.13 0-.336.05-.515.238-.18.19-.674.648-.674 1.584s.687 1.84.785 1.967c.098.124 1.36 2.044 3.291 2.87.46.195.817.313 1.098.402.46.143.882.123 1.215.074.371-.056 1.14-.46 1.3-.903l-.003-.003c.16-.444.16-.825.11-.903-.048-.078-.176-.128-.368-.222-.19-.098-1.139-.558-1.315-.62-.176-.065-.306-.098-.433.094-.127.193-.499.621-.61.748-.114.127-.225.144-.418.049s-.812-.294-1.549-.943a5.8 5.8 0 0 1-1.068-1.317c-.11-.189-.014-.294.084-.388.088-.085.193-.222.291-.332s.126-.19.192-.317c.065-.127.033-.24-.016-.334s-.434-1.03-.594-1.412c-.156-.372-.316-.32-.433-.326-.111-.007-.24-.006-.37-.006" />
                </SvgElement>
            );
        case "share/linkedin":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0M7.14 5c.804-.018 1.542.423 1.577 1.316.045 1.177-.953 1.667-2.012 1.43-1.395-.311-1.311-2.45.09-2.709q.172-.032.346-.037m7.975 3.709c1.159-.04 2.332.56 2.838 1.756.125.296.345 1.078.346 1.38V17h-2.713v-4.75c0-.4-.344-1.05-.711-1.25-.82-.447-2.12.228-2.12 1.135V17h-2.712V8.94h2.713v1.035c.565-.82 1.458-1.236 2.36-1.266m-9.199.229h2.625V17H5.916l-.088-.088V9.025z" />
                </SvgElement>
            );
        case "share/url":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0m2.32 5.459c1.063 0 2.125.404 2.934 1.213a4.154 4.154 0 0 1 0 5.869l-1.266 1.264a.75.75 0 0 1-1.06-1.06l1.265-1.265a2.65 2.65 0 0 0 0-3.746 2.65 2.65 0 0 0-3.746 0l-1.265 1.264a.75.75 0 0 1-1.06-1.06l1.265-1.266a4.13 4.13 0 0 1 2.933-1.213m.14 3.258a.749.749 0 0 1 .532 1.28l-4.996 4.995a.75.75 0 0 1-1.06-1.06l4.996-4.996a.75.75 0 0 1 .529-.22M8.47 9.902A.748.748 0 0 1 9 11.182l-1.266 1.265a2.65 2.65 0 0 0 0 3.746 2.65 2.65 0 0 0 3.746 0l1.266-1.265a.75.75 0 0 1 1.06 1.06l-1.265 1.266a4.15 4.15 0 0 1-5.867 0 4.15 4.15 0 0 1 0-5.867l1.263-1.266a.75.75 0 0 1 .532-.219" />
                </SvgElement>
            );
        case "share/email":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0M5.352 7.56H18.65L12 12.31zM5 8.437l7 5 7-5v8H5z" />
                </SvgElement>
            );

        case "filter":
            return (
                <SvgElement viewBox="0 0 24 24" {...props}>
                    <path d="M16 19H8V17H16V19ZM18 13H6V11H18V13ZM20 7H4V5H20V7Z" />
                </SvgElement>
            );

        default:
            name satisfies never;
            return null;
    }
};
